/**
 * CalendarPage  — /calendar
 *
 * Clean timeline/agenda view of the next 3 months showing
 * card-sending holidays and the user's personal occasions.
 * No calendar grid — just a scannable, action-oriented event feed.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardNav from '../components/dashboard/DashboardNav';
import CardDesignPicker from '../components/cards/CardDesignPicker';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { deriveGreeting } from '../lib/greeting';
import type { Contact, Occasion, OccasionType } from '../../../shared/src/types/database';

// ── Types ─────────────────────────────────────────────────────────────────────

type ContactWithOccasions = Contact & { occasions: Occasion[] };

interface HolidayDef {
  id: string;
  name: string;
  emoji: string;
  category: 'greeting' | 'religious' | 'seasonal';
  occasionType: OccasionType;
  customName?: string;
  getDate: (year: number) => string | null;
}

interface OccEntry {
  id: string;
  contact_id: string;
  contact_name: string;
  occasion_type: OccasionType;
  custom_occasion_name: string | null;
  next_send_date: string | null;
  card_type?: string;
}

interface TimelineEvent {
  date: string;
  kind: 'holiday' | 'occasion';
  holiday?: HolidayDef;
  scheduledCount?: number;
  occ?: OccEntry;
}

interface DrawerState {
  holiday: HolidayDef;
  date: string;
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function pad2(n: number) { return String(n).padStart(2, '0'); }
function ymd(y: number, m: number, d: number) { return `${y}-${pad2(m)}-${pad2(d)}`; }

function todayStr() {
  const n = new Date();
  return ymd(n.getFullYear(), n.getMonth() + 1, n.getDate());
}

function addMonths(base: Date, n: number): Date {
  return new Date(base.getFullYear(), base.getMonth() + n, 1);
}

function startOfThisMonth(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), 1);
}

function daysUntil(dateStr: string): number {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(dateStr + 'T00:00:00').getTime() - now.getTime()) / 86_400_000);
}

function formatDateShort(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// ── Floating holiday computations ─────────────────────────────────────────────

function nthWeekday(year: number, month: number, weekday: number, n: number): string {
  let count = 0;
  for (let day = 1; day <= 31; day++) {
    const d = new Date(year, month - 1, day);
    if (d.getMonth() !== month - 1) break;
    if (d.getDay() === weekday && ++count === n) return ymd(year, month, day);
  }
  throw new Error('nthWeekday: no match');
}

function mothersDay(year: number)   { return nthWeekday(year, 5, 0, 2); }
function fathersDay(year: number)   { return nthWeekday(year, 6, 0, 3); }
function thanksgiving(year: number) { return nthWeekday(year, 11, 4, 4); }

function easter(year: number): string {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m2 = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m2 + 114) / 31);
  const day   = ((h + l - 7 * m2 + 114) % 31) + 1;
  return ymd(year, month, day);
}

const HANUKKAH: Record<number, string> = {
  2024: '2024-12-25', 2025: '2025-12-14', 2026: '2026-12-04',
  2027: '2027-12-24', 2028: '2028-12-12', 2029: '2029-11-30',
  2030: '2030-12-20', 2031: '2031-12-09', 2032: '2032-11-27',
};
const DIWALI: Record<number, string> = {
  2024: '2024-11-01', 2025: '2025-10-20', 2026: '2026-11-08',
  2027: '2027-10-29', 2028: '2028-10-17', 2029: '2029-11-05',
  2030: '2030-10-26', 2031: '2031-11-14', 2032: '2032-11-02',
};
const EID: Record<number, string> = {
  2024: '2024-04-10', 2025: '2025-03-30', 2026: '2026-03-20',
  2027: '2027-03-09', 2028: '2028-02-26', 2029: '2029-02-14',
  2030: '2030-02-04', 2031: '2031-01-24', 2032: '2032-01-14',
};

// ── Holiday catalog (card-sending holidays only) ───────────────────────────────

const HOLIDAYS: HolidayDef[] = [
  { id: 'valentines',  name: "Valentine's Day", emoji: '💌', category: 'greeting',  occasionType: 'valentines',  getDate: (y) => `${y}-02-14` },
  { id: 'easter',      name: 'Easter',           emoji: '🐣', category: 'religious', occasionType: 'custom', customName: 'Easter',       getDate: easter },
  { id: 'mothers-day', name: "Mother's Day",     emoji: '💐', category: 'greeting',  occasionType: 'mothers_day', getDate: mothersDay },
  { id: 'fathers-day', name: "Father's Day",     emoji: '👔', category: 'greeting',  occasionType: 'fathers_day', getDate: fathersDay },
  { id: 'thanksgiving',name: 'Thanksgiving',      emoji: '🦃', category: 'seasonal',  occasionType: 'custom', customName: 'Thanksgiving', getDate: thanksgiving },
  { id: 'hanukkah',   name: 'Hanukkah',           emoji: '🕎', category: 'religious', occasionType: 'custom', customName: 'Hanukkah',     getDate: (y) => HANUKKAH[y] ?? null },
  { id: 'christmas',   name: 'Christmas',          emoji: '🎄', category: 'religious', occasionType: 'christmas',  getDate: (y) => `${y}-12-25` },
  { id: 'diwali',     name: 'Diwali',              emoji: '🪔', category: 'religious', occasionType: 'custom', customName: 'Diwali',       getDate: (y) => DIWALI[y] ?? null },
  { id: 'eid',        name: 'Eid al-Fitr',         emoji: '🌙', category: 'religious', occasionType: 'custom', customName: 'Eid al-Fitr',  getDate: (y) => EID[y]    ?? null },
];

const CAT_PILL: Record<string, string> = {
  greeting:  'bg-rose-50 text-rose-600 border-rose-100',
  religious: 'bg-violet-50 text-violet-600 border-violet-100',
  seasonal:  'bg-amber-50 text-amber-600 border-amber-100',
};

const OCC_EMOJI: Record<string, string> = {
  birthday: '🎂', anniversary: '💍', christmas: '🎄', valentines: '💌',
  mothers_day: '💐', fathers_day: '👔', graduation: '🎓',
  thinking_of_you: '💭', custom: '✨',
};

const OCC_LABEL: Record<string, string> = {
  birthday: 'Birthday', anniversary: 'Anniversary', christmas: 'Christmas',
  valentines: "Valentine's Day", mothers_day: "Mother's Day",
  fathers_day: "Father's Day", graduation: 'Graduation',
  thinking_of_you: 'Thinking of You', custom: 'Special Occasion',
};

// ── Data hook ─────────────────────────────────────────────────────────────────

function useContacts() {
  const [contacts, setContacts] = useState<ContactWithOccasions[]>([]);
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    try {
      const json = await apiFetch<{ contacts: ContactWithOccasions[] }>('/api/contacts');
      setContacts(json.contacts ?? []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  return { contacts, loading, reload: load };
}

// ── Build timeline ────────────────────────────────────────────────────────────

function buildTimeline(
  contacts: ContactWithOccasions[],
  windowStart: Date,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const today = todayStr();
  const allOccasions = contacts.flatMap(c => c.occasions.filter(o => !o.deleted_at));

  // Generate the 3 months
  const months = [0, 1, 2].map(i => addMonths(windowStart, i));

  // ── Holidays ──
  for (const h of HOLIDAYS) {
    for (const monthDate of months) {
      const year  = monthDate.getFullYear();
      const month = monthDate.getMonth() + 1;
      const date  = h.getDate(year);
      if (!date) continue;
      // Only include if this date falls in this month
      if (parseInt(date.split('-')[1]) !== month) continue;
      if (date < today) continue;

      const scheduledCount = allOccasions.filter(o =>
        o.occasion_type === h.occasionType &&
        (h.occasionType !== 'custom' || o.custom_occasion_name === (h.customName ?? null))
      ).length;
      events.push({ date, kind: 'holiday', holiday: h, scheduledCount });
    }
  }

  // ── Personal occasions ──
  for (const contact of contacts) {
    for (const occ of contact.occasions) {
      if (occ.deleted_at) continue;

      for (const monthDate of months) {
        const year  = monthDate.getFullYear();
        const month = monthDate.getMonth() + 1;
        let dateStr: string | null = null;

        if (occ.occasion_type === 'mothers_day') {
          const d = mothersDay(year);
          if (parseInt(d.split('-')[1]) === month) dateStr = d;
        } else if (occ.occasion_type === 'fathers_day') {
          const d = fathersDay(year);
          if (parseInt(d.split('-')[1]) === month) dateStr = d;
        } else {
          const parts = occ.occasion_date.split('-');
          if (parts.length === 3 && parseInt(parts[1]) === month) {
            const test = new Date(year, month - 1, parseInt(parts[2]));
            if (test.getMonth() + 1 === month)
              dateStr = ymd(year, month, parseInt(parts[2]));
          }
        }

        if (!dateStr || dateStr < today) continue;

        events.push({
          date: dateStr,
          kind: 'occasion',
          occ: {
            id: occ.id,
            contact_id: contact.id,
            contact_name: contact.name,
            occasion_type: occ.occasion_type,
            custom_occasion_name: occ.custom_occasion_name,
            next_send_date: occ.next_send_date,
            card_type: contact.card_type,
          },
        });
      }
    }
  }

  // Sort by date, holidays before occasions on the same day
  events.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    if (a.kind === 'holiday' && b.kind !== 'holiday') return -1;
    if (a.kind !== 'holiday' && b.kind === 'holiday') return 1;
    return 0;
  });

  // Deduplicate holidays (can appear once per window, not per month)
  const seenHolidays = new Set<string>();
  return events.filter(e => {
    if (e.kind !== 'holiday' || !e.holiday) return true;
    if (seenHolidays.has(e.holiday.id)) return false;
    seenHolidays.add(e.holiday.id);
    return true;
  });
}

// ── Card setup step (step 2 of the drawer) ───────────────────────────────────

interface SetupContact {
  id: string;
  name: string;
  relationshipType: string | null;
}

function CardSetupStep({
  holiday,
  date,
  contact,
  onBack,
  onScheduled,
}: {
  holiday:     HolidayDef;
  date:        string;
  contact:     SetupContact;
  onBack:      () => void;
  onScheduled: (contactId: string, occasionId: string) => void;
}) {
  const { profile } = useAuth();
  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(null);
  const [greeting, setGreeting] = useState(() =>
    deriveGreeting(contact.relationshipType, holiday.occasionType, contact.name)
  );
  const [message,  setMessage ] = useState('');
  const [saving,   setSaving  ] = useState(false);
  const [error,    setError   ] = useState('');

  // Check if the holiday falls within the 14-day fulfillment lead window
  const todayStr  = new Date().toISOString().split('T')[0];
  const daysUntil = Math.ceil(
    (new Date(`${date}T00:00:00`).getTime() - new Date(`${todayStr}T00:00:00`).getTime()) / 86_400_000,
  );
  const tooSoon = daysUntil >= 0 && daysUntil < 14;

  async function handleSchedule() {
    setSaving(true);
    setError('');
    try {
      // 1. Create the occasion
      const res = await apiFetch<{ occasion: { id: string } }>('/api/occasions', {
        method: 'POST',
        body: JSON.stringify({
          contact_id:           contact.id,
          occasion_type:        holiday.occasionType,
          occasion_date:        date,
          recurring:            true,
          custom_occasion_name: holiday.customName ?? null,
        }),
      });
      // 2. Attach design + greeting + message in one PUT
      await apiFetch(`/api/occasions/${res.occasion.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          card_design_id:  selectedDesignId,
          card_greeting:   greeting,
          current_message: message.trim() || null,
        }),
      });
      onScheduled(contact.id, res.occasion.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col h-full">

      {/* Step header */}
      <div className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-gray-100">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 font-body text-sm text-gray-400 hover:text-navy transition-colors mb-3"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to contacts
        </button>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-navy flex items-center justify-center flex-shrink-0">
            <span className="font-body text-xs font-semibold text-white">
              {contact.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
          <div>
            <p className="font-heading text-base font-semibold text-navy leading-tight">{contact.name}</p>
            <p className="font-body text-xs text-gray-400">{holiday.emoji} {holiday.name}</p>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <p className="font-body text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Lead-time notice — holiday is less than 14 days away */}
        {tooSoon && (
          <div className="rounded-xl border px-4 py-3 flex gap-3 items-start"
               style={{ backgroundColor: '#FFFBEB', borderColor: '#FDE68A' }}>
            <span className="text-lg leading-none mt-0.5">⏰</span>
            <div>
              <p className="font-body text-sm font-semibold" style={{ color: '#92400E' }}>
                {holiday.name} is {daysUntil === 0 ? 'today' : `${daysUntil} day${daysUntil === 1 ? '' : 's'} away`}
              </p>
              <p className="font-body text-xs mt-0.5" style={{ color: '#92400E' }}>
                We need at least 14 days to print and mail, your first card will be
                sent for {holiday.name} next year instead.
              </p>
            </div>
          </div>
        )}

        {/* Card design */}
        <div>
          <p className="font-body text-sm font-semibold text-navy mb-1">
            Pick a card design
          </p>
          <p className="font-body text-xs text-gray-400 mb-3">
            Optional, you can always change it later from your dashboard.
          </p>
          <CardDesignPicker
            occasionType={holiday.occasionType}
            selected={selectedDesignId}
            onChange={setSelectedDesignId}
            cardGreeting={greeting}
            cardClosing={profile?.card_closing ?? 'With love,'}
            cardFromLine={profile?.card_from_line ?? profile?.first_name ?? ''}
          />
        </div>

        {/* Greeting */}
        <div>
          <label className="font-body text-sm font-semibold text-navy block mb-1">
            Card greeting
          </label>
          <p className="font-body text-xs text-gray-400 mb-2">
            The opening line printed inside the card.
          </p>
          <input
            type="text"
            value={greeting}
            onChange={(e) => setGreeting(e.target.value)}
            placeholder="Dear [Name],"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5
                       font-body text-sm text-navy placeholder-gray-400
                       focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage/60
                       transition-colors"
          />
        </div>

        {/* Personal message */}
        <div>
          <label className="font-body text-sm font-semibold text-navy block mb-1">
            Personal message
          </label>
          <p className="font-body text-xs text-gray-400 mb-2">
            Optional, a note printed inside the card just for them.
          </p>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder={`Wishing you a wonderful ${holiday.name}!`}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5
                       font-body text-sm text-navy placeholder-gray-400 resize-none
                       focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage/60
                       transition-colors"
          />
        </div>

      </div>

      {/* Footer CTA */}
      <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100">
        <button
          type="button"
          onClick={handleSchedule}
          disabled={saving}
          className="btn-primary w-full disabled:opacity-60"
        >
          {saving ? 'Scheduling…' : `Schedule card for ${contact.name}`}
        </button>
      </div>
    </div>
  );
}

// ── Drawer ────────────────────────────────────────────────────────────────────

function HolidayDrawer({
  state, contacts, onClose, onAdded,
}: {
  state: DrawerState;
  contacts: ContactWithOccasions[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const navigate = useNavigate();
  const { holiday, date } = state;
  const [error, setError]           = useState('');
  const [setupFor, setSetupFor]     = useState<SetupContact | null>(null);
  // contactId → occasionId for contacts scheduled this session
  const [localScheduled, setLocalScheduled] = useState<Map<string, string>>(new Map());
  // contactIds removed this session (optimistic hide before reload)
  const [localRemoved, setLocalRemoved]     = useState<Set<string>>(new Set());
  // which contact row is showing its remove-confirm prompt
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [removing, setRemoving]               = useState(false);

  const allOccasions = contacts.flatMap(c => c.occasions.filter(o => !o.deleted_at));

  const [y, m, d] = date.split('-').map(Number);
  const dateLabel = new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  // Map of contactId → occasionId for this holiday (existing + this session)
  const scheduledMap = new Map<string, string>([
    ...allOccasions
      .filter(o => o.occasion_type === holiday.occasionType && !localRemoved.has(o.contact_id))
      .map(o => [o.contact_id, o.id] as [string, string]),
    ...[...localScheduled].filter(([cid]) => !localRemoved.has(cid)),
  ]);

  useEffect(() => {
    setError('');
    setSetupFor(null);
    setLocalScheduled(new Map());
    setLocalRemoved(new Set());
    setConfirmRemoveId(null);
  }, [state]);

  function handleScheduled(contactId: string, occasionId: string) {
    setLocalScheduled(prev => new Map([...prev, [contactId, occasionId]]));
    setSetupFor(null);
    onAdded();
  }

  async function handleRemoveContact(contactId: string) {
    const occasionId = scheduledMap.get(contactId);
    if (!occasionId) return;
    setRemoving(true);
    setError('');
    try {
      await apiFetch(`/api/occasions/${occasionId}`, { method: 'DELETE' });
      setLocalRemoved(prev => new Set([...prev, contactId]));
      setConfirmRemoveId(null);
      onAdded(); // refresh timeline counts
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove. Please try again.');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-sm bg-white shadow-2xl flex flex-col">

        {/* ── Step 2: card setup ─── */}
        {setupFor ? (
          <CardSetupStep
            holiday={holiday}
            date={date}
            contact={setupFor}
            onBack={() => setSetupFor(null)}
            onScheduled={handleScheduled}
          />
        ) : (

          /* ── Step 1: contact list ─── */
          <>
            {/* Header */}
            <div className="p-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-body text-xs font-semibold text-sage uppercase tracking-widest mb-1">{dateLabel}</p>
                  <h2 className="font-heading text-2xl text-navy">{holiday.emoji} {holiday.name}</h2>
                </div>
                <button type="button" onClick={onClose}
                  className="rounded-full p-2 hover:bg-gray-100 transition-colors -mt-1 -mr-1">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contact list body */}
            <div className="flex-1 overflow-y-auto p-5">

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="font-body text-sm text-red-600">{error}</p>
                </div>
              )}

              {contacts.length === 0 ? (
                <div className="text-center py-10">
                  <p className="font-body text-sm text-gray-400 mb-4">No contacts yet.</p>
                  <button type="button" onClick={() => navigate('/dashboard')}
                    className="btn-primary text-sm">Add your first contact</button>
                </div>
              ) : (
                <>
                  <p className="font-body text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                    Schedule cards for your contacts
                  </p>
                  <div className="space-y-2">
                    {contacts.map(contact => {
                      const scheduled = scheduledMap.has(contact.id);
                      const confirming = confirmRemoveId === contact.id;
                      return (
                        <div key={contact.id}
                          className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">

                          {/* Main row */}
                          <div className="flex items-center gap-3 p-3">
                            <div className="h-9 w-9 rounded-full bg-navy flex items-center justify-center flex-shrink-0">
                              <span className="font-body text-xs font-semibold text-white">
                                {contact.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-body text-sm font-medium text-gray-800 truncate">{contact.name}</p>
                              {contact.relationship_type && (
                                <p className="font-body text-xs text-gray-400 capitalize">
                                  {contact.relationship_type.replace(/_/g, ' ')}
                                </p>
                              )}
                            </div>
                            {scheduled ? (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="flex items-center gap-1 text-emerald-600">
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span className="font-body text-xs font-semibold">Scheduled</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setConfirmRemoveId(confirming ? null : contact.id)}
                                  className="rounded-full p-1 text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors"
                                  aria-label="Remove"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setSetupFor({
                                  id: contact.id,
                                  name: contact.name,
                                  relationshipType: contact.relationship_type ?? null,
                                })}
                                className="flex items-center gap-1 font-body text-xs font-semibold text-sage border border-sage/40 rounded-lg px-3 py-1.5 hover:bg-sage/5 transition-colors flex-shrink-0"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                </svg>
                                Add card
                              </button>
                            )}
                          </div>

                          {/* Inline remove confirm */}
                          {confirming && (
                            <div className="border-t border-red-100 bg-red-50 px-3 py-2.5 flex items-center justify-between gap-3">
                              <p className="font-body text-xs text-red-700">Remove {contact.name}'s card?</p>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setConfirmRemoveId(null)}
                                  disabled={removing}
                                  className="font-body text-xs text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                  Keep
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveContact(contact.id)}
                                  disabled={removing}
                                  className="font-body text-xs font-semibold text-red-600 bg-white border border-red-200 rounded-lg px-2.5 py-1 hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50"
                                >
                                  {removing ? 'Removing…' : 'Yes, remove'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 rounded-xl bg-sage/5 border border-sage/20 px-4 py-3">
                    <p className="font-body text-xs text-gray-500 mb-2">
                      Missing someone? (e.g. a stepdad, father-in-law)
                    </p>
                    <button type="button" onClick={() => navigate('/dashboard')}
                      className="flex items-center gap-1.5 font-body text-xs font-semibold text-sage hover:text-navy transition-colors">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      Add a new contact on your dashboard
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CalendarPage() {
  const [windowStart, setWindowStart] = useState(startOfThisMonth);
  const [drawer, setDrawer]           = useState<DrawerState | null>(null);
  const { contacts, loading, reload } = useContacts();

  const months = useMemo(() => [0, 1, 2].map(i => addMonths(windowStart, i)), [windowStart]);
  const timeline = useMemo(() => buildTimeline(contacts, windowStart), [contacts, windowStart]);

  // Group timeline events by month bucket (YYYY-MM)
  const grouped = useMemo(() => {
    const map = new Map<string, TimelineEvent[]>();
    for (const evt of timeline) {
      const key = evt.date.slice(0, 7); // "YYYY-MM"
      const arr = map.get(key) ?? [];
      arr.push(evt);
      map.set(key, arr);
    }
    // Return in month order
    return months
      .map(m => {
        const key = `${m.getFullYear()}-${pad2(m.getMonth() + 1)}`;
        return { month: m, key, events: map.get(key) ?? [] };
      });
  }, [timeline, months]);

  // Attention: major upcoming holidays with no contacts scheduled
  const attentionHolidays = useMemo(() => {
    const today = todayStr();
    const allOccs = contacts.flatMap(c => c.occasions.filter(o => !o.deleted_at));
    return timeline.filter(e => {
      if (e.kind !== 'holiday' || !e.holiday) return false;
      if (e.date < today) return false;
      if (!['valentines', 'mothers_day', 'fathers_day', 'christmas'].includes(e.holiday.occasionType)) return false;
      return (e.scheduledCount ?? 0) === 0 && contacts.length > 0;
    });
  }, [timeline, contacts]);

  const rangeLabel = (() => {
    const s = months[0], e = months[2];
    const sm = s.toLocaleDateString('en-US', { month: 'short' });
    const em = e.toLocaleDateString('en-US', { month: 'short' });
    return s.getFullYear() === e.getFullYear()
      ? `${sm} – ${em} ${s.getFullYear()}`
      : `${sm} ${s.getFullYear()} – ${em} ${e.getFullYear()}`;
  })();

  const totalEvents = timeline.length;

  return (
    <div className="min-h-screen bg-cream pb-24 sm:pb-10">
      <DashboardNav />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-navy">Upcoming Events</h1>
          <p className="font-body text-sm text-gray-400 mt-1">
            Tap any holiday to schedule cards for your contacts
          </p>

          {/* Period nav — centered range label between arrows, like a native calendar */}
          <div className="flex items-center gap-3 mt-4">
            <button
              type="button"
              onClick={() => setWindowStart(d => addMonths(d, -3))}
              className="flex items-center gap-1.5 font-body text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-xl px-3 py-2 hover:bg-gray-50 active:scale-95 transition-all"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Prev
            </button>

            <div className="flex-1 text-center">
              <p className="font-heading text-base font-semibold text-navy">{rangeLabel}</p>
            </div>

            <button
              type="button"
              onClick={() => setWindowStart(d => addMonths(d, 3))}
              className="flex items-center gap-1.5 font-body text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-xl px-3 py-2 hover:bg-gray-50 active:scale-95 transition-all"
            >
              Next
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Attention banner ────────────────────────────────────────────── */}
        {attentionHolidays.length > 0 && (
          <div className="mb-5 bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl flex-shrink-0">🔔</span>
              <div className="space-y-2 flex-1">
                <p className="font-body text-sm font-semibold text-amber-900">
                  You have upcoming holidays with no cards scheduled
                </p>
                {attentionHolidays.map(e => (
                  <button
                    key={e.holiday!.id}
                    type="button"
                    onClick={() => setDrawer({ holiday: e.holiday!, date: e.date })}
                    className="w-full flex items-center justify-between bg-white/70 rounded-xl px-3 py-2.5 hover:bg-white transition-colors text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-base">{e.holiday!.emoji}</span>
                      <div>
                        <p className="font-body text-sm font-semibold text-amber-900">{e.holiday!.name}</p>
                        <p className="font-body text-xs text-amber-700">in {daysUntil(e.date)} days · tap to schedule cards</p>
                      </div>
                    </div>
                    <svg className="h-4 w-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Loading ─────────────────────────────────────────────────────── */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 rounded-full border-2 border-sage border-t-transparent animate-spin" />
          </div>
        )}

        {/* ── Timeline ────────────────────────────────────────────────────── */}
        {!loading && (
          <>
            {totalEvents === 0 ? (
              <div className="bg-white rounded-2xl shadow-warm p-10 text-center">
                <p className="text-3xl mb-3">📭</p>
                <p className="font-heading text-lg text-navy mb-1">Nothing coming up</p>
                <p className="font-body text-sm text-gray-400 mb-5">
                  No holidays or occasions in this window. Try moving forward a few months.
                </p>
                <button type="button" onClick={() => setWindowStart(d => addMonths(d, 3))}
                  className="btn-primary text-sm">Next 3 months →</button>
              </div>
            ) : (
              <div className="space-y-8">
                {grouped.map(({ month, key, events }) => (
                  <section key={key}>
                    {/* Month header */}
                    <div className="flex items-center gap-3 mb-3">
                      <h2 className="font-heading text-lg font-semibold text-navy">
                        {formatMonthYear(month)}
                      </h2>
                      <div className="flex-1 h-px bg-gray-200" />
                      {events.length > 0 && (
                        <span className="font-body text-xs text-gray-400">
                          {events.length} event{events.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {events.length === 0 ? (
                      <p className="font-body text-sm text-gray-400 pl-1">No events this month.</p>
                    ) : (
                      <div className="space-y-2">
                        {events.map((evt, i) => (
                          evt.kind === 'holiday' && evt.holiday ? (
                            <HolidayRow
                              key={`h-${evt.holiday.id}-${evt.date}`}
                              evt={evt}
                              totalContacts={contacts.length}
                              onClick={() => setDrawer({ holiday: evt.holiday!, date: evt.date })}
                            />
                          ) : evt.occ ? (
                            <OccasionRow key={`o-${evt.occ.id}-${i}`} evt={evt} onRemoved={reload} />
                          ) : null
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Drawer ─────────────────────────────────────────────────────────── */}
      {drawer && (
        <HolidayDrawer
          state={drawer}
          contacts={contacts}
          onClose={() => setDrawer(null)}
          onAdded={reload}
        />
      )}
    </div>
  );
}

// ── Event row components ──────────────────────────────────────────────────────

function HolidayRow({
  evt, totalContacts, onClick,
}: {
  evt: TimelineEvent;
  totalContacts: number;
  onClick: () => void;
}) {
  const { holiday, date, scheduledCount = 0 } = evt;
  if (!holiday) return null;

  const allScheduled  = scheduledCount >= totalContacts && totalContacts > 0;
  const someScheduled = scheduledCount > 0 && !allScheduled;
  const noneScheduled = scheduledCount === 0;

  return (
    <button type="button" onClick={onClick}
      className="w-full text-left bg-white rounded-2xl shadow-warm px-5 py-4 flex items-center gap-4 hover:shadow-md transition-shadow group">

      {/* Date */}
      <div className="flex-shrink-0 w-12 text-center">
        <p className="font-body text-xs text-gray-400 leading-none mb-0.5">
          {formatDateShort(date).split(' ')[0]}
        </p>
        <p className="font-heading text-xl font-bold text-navy leading-none">
          {formatDateShort(date).split(' ')[1]}
        </p>
      </div>

      {/* Divider */}
      <div className="w-px h-10 bg-gray-100 flex-shrink-0" />

      {/* Emoji */}
      <span className="text-2xl flex-shrink-0">{holiday.emoji}</span>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-body text-sm font-semibold text-gray-900">{holiday.name}</p>
          <span className={`font-body text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${CAT_PILL[holiday.category]}`}>
            {holiday.category}
          </span>
        </div>
        <p className={`font-body text-xs mt-0.5
          ${allScheduled  ? 'text-emerald-600' :
            someScheduled ? 'text-amber-600'   : 'text-gray-400'}`}>
          {totalContacts === 0
            ? 'Tap to schedule cards'
            : allScheduled
            ? `✓ ${scheduledCount} scheduled · tap to add more`
            : someScheduled
            ? `${scheduledCount} of ${totalContacts} scheduled · tap to add more`
            : 'Tap to schedule cards'}
        </p>
      </div>

      {/* Always show a chevron — this row is always tappable */}
      <svg
        className={`h-4 w-4 flex-shrink-0 transition-colors
          ${allScheduled ? 'text-emerald-400 group-hover:text-emerald-600' : 'text-gray-300 group-hover:text-sage'}`}
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

function OccasionRow({ evt, onRemoved }: { evt: TimelineEvent; onRemoved?: () => void }) {
  const { occ, date } = evt;
  const { profile } = useAuth();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing,      setRemoving     ] = useState(false);

  if (!occ) return null;

  const label = occ.custom_occasion_name ?? OCC_LABEL[occ.occasion_type] ?? occ.occasion_type;
  const days  = daysUntil(date);

  const mailDate = (() => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() - 14);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  })();

  async function handleRemove() {
    setRemoving(true);
    try {
      await apiFetch(`/api/occasions/${occ!.id}`, { method: 'DELETE' });
      onRemoved?.();
    } catch {
      setRemoving(false);
      setConfirmRemove(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-warm overflow-hidden">
      <div className="px-5 py-4 flex items-center gap-4">

        {/* Date */}
        <div className="flex-shrink-0 w-12 text-center">
          <p className="font-body text-xs text-gray-400 leading-none mb-0.5">
            {formatDateShort(date).split(' ')[0]}
          </p>
          <p className="font-heading text-xl font-bold text-navy leading-none">
            {formatDateShort(date).split(' ')[1]}
          </p>
        </div>

        {/* Divider */}
        <div className="w-px h-10 bg-gray-100 flex-shrink-0" />

        {/* Emoji */}
        <span className="text-2xl flex-shrink-0">{OCC_EMOJI[occ.occasion_type] ?? '✨'}</span>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="font-body text-sm font-semibold text-gray-900 truncate">
            {occ.contact_name}
            <span className="font-normal text-gray-400"> · {label}</span>
          </p>
          <p className="font-body text-xs text-emerald-600 mt-0.5">
            ✓ Card scheduled · mails {mailDate}
            {days <= 30 && (
              <span className="text-gray-400 ml-1">({days}d away)</span>
            )}
          </p>
        </div>

        {/* Right: checkmark + trash */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <svg className="h-5 w-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {onRemoved && (
            <button
              type="button"
              onClick={() => setConfirmRemove(true)}
              className="rounded-full p-1 text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors"
              aria-label="Remove"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Inline remove confirm */}
      {confirmRemove && (
        <div className="border-t border-red-100 bg-red-50 px-5 py-2.5 flex items-center justify-between gap-3">
          <p className="font-body text-xs text-red-700">
            Remove {occ.contact_name}'s {label} card?
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setConfirmRemove(false)}
              disabled={removing}
              className="font-body text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Keep
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={removing}
              className="font-body text-xs font-semibold text-red-600 bg-white border border-red-200 rounded-lg px-2.5 py-1 hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50"
            >
              {removing ? 'Removing…' : 'Yes, remove'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
